import { useCallback, useEffect, useState } from "react";

/**
 * Home-screen install. There is no store: the site itself is the app. Chrome
 * on Android hands us a prompt we can fire from a button; iOS never does, so
 * the page has to show Safari's own three taps. Links opened from Telegram or
 * Facebook land in an in-app browser where neither works, and the honest
 * answer there is "open this in Chrome or Safari first".
 */

export interface PlatformInfo {
  /** Already running from the home screen. */
  standalone: boolean;
  ios: boolean;
  android: boolean;
  /** Telegram, Facebook, Instagram, Line, TikTok and friends. */
  inApp: boolean;
}

export function platformInfo(): PlatformInfo {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return { standalone: false, ios: false, android: false, inApp: false };
  }
  const ua = navigator.userAgent;
  const nav = navigator as Navigator & { standalone?: boolean };
  const standalone =
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
    nav.standalone === true;
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const inAppUa = /FBAN|FBAV|FB_IAB|Instagram|Line\/|Telegram|TikTok|Messenger|; wv\)/i.test(ua);
  // an iOS web view is Safari's engine without Safari's name
  const iosWebView = ios && !standalone && !/Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return { standalone, ios, android, inApp: inAppUa || iosWebView };
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Chrome's install prompt, held until the customer taps the button. */
export function useInstallPrompt(): {
  canPrompt: boolean;
  installed: boolean;
  prompt: () => Promise<"accepted" | "dismissed" | "none">;
} {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const prompt = useCallback(async () => {
    if (!event) return "none" as const;
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setEvent(null);
    return outcome;
  }, [event]);

  return { canPrompt: event !== null, installed, prompt };
}

const HINT_KEY = "moomoo.installhint.v1";

/** Whether the shop should still nudge towards installing. */
export function installHintDismissed(): boolean {
  try {
    return localStorage.getItem(HINT_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissInstallHint(): void {
  try {
    localStorage.setItem(HINT_KEY, "1");
  } catch {
    // private mode: the hint simply comes back next time
  }
}
